import React, { useEffect, useState } from 'react';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownList,
  Table,
  TableData,
  TableRow,
  ToastMessage
} from '@cokitchen/cokitchen-components';
import { ArrowLeftIcon, DeleteIcon, EditIcon } from 'cokitchen-icons';
import './styles.scss';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as AddIcon } from 'assets/icons/add-icon.svg';
import { ReactComponent as UploadIcon } from 'assets/icons/upload-icon.svg';
import { ReactComponent as FilterIcon } from 'assets/icons/filter-icon.svg';
import SearchInput from 'components/kitchen/search-input';
import UploadTeamListModal from 'components/kitchen/upload-team-modal';
import AddMember from 'components/kitchen/add-member';
import EditMember from 'components/kitchen/edit-member';
import { KITCHEN_STAFF_ROLES } from 'utils/enum';
import { KitchenDepartmentType, StaffType } from 'models/team-list.model';
import useTeamListContext from 'contexts/team-list.context';
import { toast } from 'react-toastify';
import { apiService } from 'utils/api';
import moment from 'moment';
import Loader from 'components/base/loader';
import DeleteStaffModal from 'components/kitchen/modals/delete-staff-modal';
import { getUserName } from 'utils/functions';

interface Params {
  page: number;
  search?: string;
  department_id?: string;
  role?: string;
}

interface StaffToDelete {
  name: string;
  id: string;
}

const TeamList = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [showEditMember, setShowEditMember] = useState(false);
  const [showDeleteMember, setShowDeleteMember] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState({} as KitchenDepartmentType);
  const [pageCount, setPageCount] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [staff, setStaff] = useState<StaffType[]>([]);
  const [staffToDelete, setStaffToDelete] = useState({} as StaffToDelete);

  const { departments, setEditMemberFormData } = useTeamListContext();

  const tableHeaders = [
    'Staff No.',
    'Staff Name',
    'Phone No.',
    'Role',
    'Department',
    'Date Added',
    ''
  ];

  const fetchTeamList = async () => {
    const params: Params = {
      page
    };
    if (search) params.search = search;
    if (selectedDepartment) params.department_id = selectedDepartment.value;
    if (selectedRole) params.role = selectedRole;
    try {
      setLoading(true);
      const res = await apiService.get('kitchen/staff', {
        baseUrl: process.env.REACT_APP_KITCHEN_API_URL,
        params
      });
      if (res.status === 200) {
        setStaff(res?.data?.data?.results);
        const totalNoOfPages = Math.ceil(res?.data?.data?.total / 40);
        setPageCount(totalNoOfPages);
      }
    } catch (error: any) {
      toast(
        <ToastMessage type='error' message={error.response.data.message || 'An error occured'} />
      );
    } finally {
      setLoading(false);
    }
  };

  const setEditFormData = (staff: StaffType & { id: string }) => {
    setEditMemberFormData({
      id: staff.id,
      first_name: staff.first_name,
      last_name: staff.last_name,
      staff_number: staff.staff_number,
      department_id: staff.department_id,
      role: staff.role,
      phone_number: staff.phone_number ? staff.phone_number.replaceAll('-', '') : '',
      email: staff.email || ''
    });
  };

  const getSelectedPage = async (val: { selected: number }) => {
    const pageNo = val.selected + 1;
    setPage(pageNo);
  };

  const resetPageToOne = () => {
    setPage(1);
  };

  const resetPage = () => {
    setPage(1);
    setSearch('');
    setSelectedRole('');
    setSelectedDepartment({} as KitchenDepartmentType);
  };

  const handlePageReset = (success: boolean | undefined) => {
    if (success) {
      resetPage();
    }
  };

  useEffect(() => {
    fetchTeamList();
  }, [page, search, selectedDepartment, selectedRole]);

  return (
    <div className='team__list'>
      <div className='flex self-start items-center gap-3 cursor-pointer mb-5'>
        <span className='p-2 text-primary bg-[#F4F1F1] rounded-full' onClick={() => navigate(-1)}>
          <ArrowLeftIcon color='#E60A2B' />
        </span>
        <p className='text-sm font-medium'>Return to settings</p>
      </div>
      <div className='flex flex-col gap-6'>
        <div className='flex items-center justify-between gap-3 pb-6 border-b-4 border-[#F5F5F5]'>
          <div className='flex flex-col gap-[2px]'>
            <p className='text-xl text-[#1A1A1A] font-semibold'>Team list</p>
            <p className='text-sm text-[#595959]'>Manage your general team members</p>
          </div>

          <div className='flex items-center gap-3'>
            <Button
              small
              variant='block'
              color='transparent'
              className='w-[119px] h-10 flex items-center gap-1 bg-[#F2F2F2] text-[#1A1A1A] font-medium'
              onClick={() => setShowBulkAdd(true)}>
              <UploadIcon className='shrink-0 w-[18px] [&_path]:fill-[#1A1A1A]' />
              Bulk add
            </Button>

            <Button
              small
              variant='block'
              color='primary'
              className='w-[129px] h-10 flex items-center gap-1'
              onClick={() => setShowAddMember(true)}>
              <AddIcon className='shrink-0' />
              Add
            </Button>
          </div>
        </div>
        <div className='flex items-center justify-between mb-10'>
          <div className='flex items-center gap-3'>
            <Dropdown
              content={
                <div className='border border-gray-border flex items-center p-[10px] min-w-[115px] justify-between rounded-lg gap-6'>
                  <span className='text-[12px] font-medium'>{selectedRole || 'Roles'}</span>
                  <div className='rounded-full text-gray-5 cursor-pointer hover:bg-gray-1 transition-all'>
                    <FilterIcon />
                  </div>
                </div>
              }>
              <DropdownList className='min-w-[190px]'>
                {Object.values(KITCHEN_STAFF_ROLES).map((r) => (
                  <DropdownItem
                    key={r}
                    className='text-sm font-medium'
                    onClick={() => {
                      setSelectedRole(r);
                      resetPageToOne();
                    }}>
                    <button className='cursor-pointer items-center flex'>{r}</button>
                  </DropdownItem>
                ))}
              </DropdownList>
            </Dropdown>
            <Dropdown
              content={
                <div className='border border-gray-border flex items-center p-[10px] min-w-[145px] justify-between rounded-lg gap-6'>
                  <span className='text-[12px] font-medium'>
                    {selectedDepartment.text || 'Departments'}
                  </span>
                  <div className='rounded-full text-gray-5 cursor-pointer hover:bg-gray-1 transition-all'>
                    <FilterIcon />
                  </div>
                </div>
              }>
              <DropdownList className='min-w-[190px]'>
                {departments.map((d) => (
                  <DropdownItem
                    key={d.value}
                    className='text-sm font-medium'
                    onClick={() => {
                      setSelectedDepartment(d);
                      resetPageToOne();
                    }}>
                    <button className='cursor-pointer items-center flex'>{d.text}</button>
                  </DropdownItem>
                ))}
              </DropdownList>
            </Dropdown>
          </div>
          <SearchInput
            value={search}
            placeholder='Search'
            onChange={(e) => {
              setSearch(e.target.value);
              resetPageToOne();
            }}
          />
        </div>
      </div>
      {loading && page === 1 ? (
        <div className='text-center mt-5'>
          <Loader />
        </div>
      ) : staff.length === 0 ? (
        <div className='text-sm text-center mt-5'>No team list available</div>
      ) : (
        <Table
          loading={loading}
          headers={tableHeaders}
          pageCount={pageCount}
          onPageChange={getSelectedPage}
          canSelect>
          {staff.map((s) => (
            <TableRow key={s.id} canSelect className='hover:bg-gray-12 table_row'>
              <TableData className='font-medium tracking-[-0.2px] text-gray-6'>
                {s.staff_number}
              </TableData>
              <TableData className='font-medium text-[#595959]'>{getUserName(s)}</TableData>
              <TableData className='font-medium text-[#595959]'>{s.phone_number}</TableData>
              <TableData className='font-medium text-gray-6'>{s.role}</TableData>
              <TableData className='font-medium text-gray-6'>{s.department.name}</TableData>
              <TableData className='text-[#595959]'>
                {moment(s.created_at).format('DD.MM.YYYY')}
              </TableData>
              <TableData>
                <div className='gap-2 flex w-[74px]'>
                  <EditIcon
                    className='w-5 h-5 text-[#6F7376] icon'
                    onClick={() => {
                      setShowEditMember(true);
                      setEditFormData(s);
                    }}
                  />
                  <DeleteIcon
                    className='w-5 h-5 text-[#E60A2B] icon'
                    onClick={() => {
                      setShowDeleteMember(true);
                      setStaffToDelete({
                        id: s.id,
                        name: getUserName(s)
                      });
                    }}
                  />
                </div>
              </TableData>
            </TableRow>
          ))}
        </Table>
      )}
      {showBulkAdd && (
        <UploadTeamListModal
          show={showBulkAdd}
          onClose={(success?: boolean) => {
            setShowBulkAdd(false);
            handlePageReset(success);
          }}
        />
      )}
      {showAddMember && (
        <AddMember
          onClose={(success?: boolean) => {
            setShowAddMember(false);
            handlePageReset(success);
          }}
        />
      )}
      {showEditMember && (
        <EditMember
          onClose={(success?: boolean) => {
            setShowEditMember(false);
            handlePageReset(success);
          }}
        />
      )}
      {showDeleteMember && (
        <DeleteStaffModal
          show={showDeleteMember}
          onClose={(success?: boolean) => {
            setShowDeleteMember(false);
            handlePageReset(success);
          }}
          staffName={staffToDelete.name}
          id={staffToDelete.id}
        />
      )}
    </div>
  );
};

export default TeamList;
